import React, { useEffect, useState, useRef } from "react";
import ActionsTable from "../../components/templates/ActionsTable";
import { Button, Space } from "antd";
import generateColumns from "./generateColumns";
import { useIntl } from "react-intl";
import { BranchSelect } from "../../components/forms/AutoCompleteComponent";
import { SyncOutlined, RedoOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "../../components/antOverrides";
import { openURL } from "../../components/util/helpers";
import PatientSchedulesEvents from "./PatientSchedulesEvents";
import moment from "moment";
import {
  syncOk2Go,
  resetOk2Go,
  syncOk2GoRow,
  setActiveBranch,
  setActiveRowId,
  setStaffId,
  setClientId,
  loadPage,
  setDate,
  exportRevaha,
  setPage,
  setJobId,
  getJobStatus,
} from "./ok2GoSlice";
import { message } from "antd";

import {
  ClientsSelect,
  EmployeesSelect,
} from "../../components/forms/AutoCompleteComponent";
import { useParams, useLocation } from "react-router-dom";
import { useCustomQuery } from "./customHooks";
import { usePermission } from "../../hooks/accessControl";

const Ok2GoTable = ({ showEmployeesSelect, showClientsSelect, type }) => {
  const { branchId } = useParams();

  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const [patientSchedulesEventsVisible, setPatientSchedulesEventsVisible] =
    useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const syncBranchLoading = useSelector(
    (state) => state.pendingActions["ok2Go/sync"]
  );
  const syncRowLoading = useSelector(
    (state) => state.pendingActions["ok2Go/sync_row"]
  );
  const resetSyncBranchLoading = useSelector(
    (state) => state.pendingActions["ok2Go/reset"]
  );
  const {
    page,
    activeBranch,
    activeSection,
    activeEntity,
    activeRowId,
    activeDate,
    jobId,
    api,
  } = useSelector((state) => state.ok2Go);
  const permission = type === "revaha" ? "ok2go_revaha" : "ok2go_btl";
  const hasEditorPermission = usePermission(activeBranch, permission, "editor");
  const branchPath = activeBranch && `branches/${activeBranch}/`;
  const { current, pageSize, order } = page;
  const { data, isLoading, refetch } = useCustomQuery(
    activeSection,
    activeBranch,
    page,
    activeDate,
    api
  );
  const pollingInterval = useRef(null);

  useEffect(() => {
    dispatch(
      setPage({ ...page, order: { field: "updated_at", order: "descend" } })
    );
  }, []);

  useEffect(() => {
    if (branchId) {
      dispatch(setActiveBranch(branchId));
    }
  }, [branchId, dispatch]);

  useEffect(() => {
    if (jobId) {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      pollingInterval.current = setInterval(async () => {
        const statusAction = await dispatch(getJobStatus(jobId));
        const status = statusAction?.payload?.status;
        const error = statusAction?.error;
        if (status === "ok") {
          message["success"](
            intl.formatMessage({
              id: `app.notifications.get_job_status_success`,
            })
          );
        } else if (error) {
          const localizedTitle = intl.formatMessage({
            id: `app.notifications.get_job_status_error`,
          });
          message["error"](`${localizedTitle}: ${error?.message}`, 4);
        }
        if (status === "ok" || error) {
          clearInterval(pollingInterval.current);
          dispatch(setJobId(null));
          dispatch(setActiveRowId(null));
          refetch();
        }
      }, 5000);
    }

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [jobId, dispatch]);

  const handleBranchChange = (val) => {
    dispatch(setActiveBranch(val));
  };
  const handleStaffChange = (val) => {
    dispatch(setStaffId(val));
  };
  const handleClientChange = (val) => {
    dispatch(setClientId(val));
  };

  const handleBtlSync = async () => {
    try {
      const month = moment(activeDate).format("M");
      const year = moment(activeDate).format("YYYY");
      const schedulesPayload = {
        year: +year,
        month: +month,
      };
      const payload = activeSection === "schedules" ? schedulesPayload : {};

      const action = await dispatch(
        syncOk2Go({ activeBranch, activeSection, payload })
      );

      const jobId = action?.payload?.data?.job_id;
      if (jobId) {
        dispatch(setJobId(jobId));
      }
    } catch (error) {
      console.error("Sync failed:", error);
    }
  };

  const handleRevahaSync = async () => {
    const entity = activeEntity.replace("_id", "s");
    const data = {
      branchId: branchId,
      params: {
        branch_ids: [activeBranch],
      },
      activeSection,
      entity,
    };
    const action = await dispatch(exportRevaha(data));
    if (!action.error) {
      openURL(action.payload.data.file_url);
    }
  };

  const handleSync = async () => {
    if (api === "btl") {
      handleBtlSync();
    } else {
      handleRevahaSync();
    }
  };

  const handleResetSync = async () => {
    const month = moment(activeDate).format("M");
    const year = moment(activeDate).format("YYYY");
    const schedulesPayload = {
      year: +year,
      month: +month,
    };
    const payload = activeSection === "schedules" ? schedulesPayload : {};
    await dispatch(resetOk2Go({ activeBranch, activeSection, payload }));
    refetch();
  };

  const handleSyncRow = async (id) => {
    try {
      const year = moment(activeDate).year();
      const month = moment(activeDate).month() + 1;

      const payload =
        activeSection === "schedules"
          ? { year, month, [activeEntity]: id }
          : { [activeEntity]: id };

      dispatch(setActiveRowId(id));
      const action = await dispatch(
        syncOk2GoRow({ activeBranch, activeSection, payload })
      );

      const jobId = action?.payload?.data?.job_id;
      if (jobId) {
        dispatch(setJobId(jobId));
      }
    } catch (error) {
      console.error("Row sync failed:", error);
    }
  };

  const handleSelectPatient = (record) => {
    setSelectedPatientId(record.patient_id);
    setPatientSchedulesEventsVisible(true);
  };

  const handleDateChange = (date) => {
    const formattedDate = date ? date.format() : null;
    dispatch(setDate({ date: formattedDate }));
  };

  const handleHidePatientSchedulesEvents = () => {
    setPatientSchedulesEventsVisible(false);
    setSelectedPatientId(null);
  };

  const columns = generateColumns(
    activeSection,
    intl,
    handleSyncRow,
    activeRowId,
    syncRowLoading,
    branchId,
    handleSelectPatient,
    hasEditorPermission,
    jobId
  );
  const staffFilter = { filter: { $and: [{ staff_member: true }] } };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2>
          {intl.formatMessage({ id: `app.settings.ok2Go_${activeSection}` })}
        </h2>
        <Space size="small">
          {showEmployeesSelect && (
            <EmployeesSelect
              placeholder={intl.formatMessage({
                id: `app.gaps.search_by_staff`,
              })}
              style={{ width: 150 }}
              onChange={handleStaffChange}
              allowClear
              extraParams={{
                ...staffFilter,
                // query: { assigned_to_employee: true },
                scopePrefix: branchId ? `branches/${branchId}/` : ``,
              }}
            />
          )}
          {showClientsSelect && (
            <ClientsSelect
              placeholder={intl.formatMessage({
                id: `app.gaps.search_by_client`,
              })}
              style={{ width: 180 }}
              onChange={handleClientChange}
              required={true}
              extraParams={{
                scopePrefix: branchId ? `branches/${branchId}/` : ``,
              }}
            />
          )}

          {activeSection !== "schedules" && activeSection !== "employees" && (
            <DatePicker
              value={activeDate && moment(activeDate)}
              onChange={handleDateChange}
              picker="month"
              format="MM/YYYY"
              placeholder={intl.formatMessage({ id: `app.gaps.select_month` })}
              allowClear={false}
            />
          )}
          {!branchId && (
            <BranchSelect
              style={{ width: 170 }}
              placeholder={intl.formatMessage({ id: `app.users.chooseBranch` })}
              onChange={handleBranchChange}
            />
          )}
          {api === "btl" &&
            activeSection === "employees" &&
            hasEditorPermission && (
              <Button
                onClick={handleResetSync}
                icon={<RedoOutlined />}
                disabled={!activeBranch}
                loading={resetSyncBranchLoading}
              >
                {intl.formatMessage({ id: `app.actions.reset_sync` })}
              </Button>
            )}
          {hasEditorPermission && (
            <Button
              type="primary"
              onClick={handleSync}
              icon={<SyncOutlined />}
              disabled={!activeBranch || syncBranchLoading || !!jobId}
              loading={syncBranchLoading || (!!jobId && !activeRowId)}
            >
              {intl.formatMessage({ id: `app.actions.sync` })}
            </Button>
          )}
        </Space>
      </div>
      <ActionsTable
        key="id"
        tableItems={columns}
        category="ok2go"
        resource="ok2go"
        current={current}
        pageSize={pageSize}
        total={data?.total}
        order={order}
        loading={isLoading}
        loadPage={loadPage}
        items={data?.items || []}
        showHeader={false}
        skipInitialLoad={true}
        rowClassName={(record) =>
          record.error_message || record.has_event_status_errors ? "error" : ""
        }
        filtersType={{ has_event_status_errors: "boolean" }}
      />
      <PatientSchedulesEvents
        onHide={handleHidePatientSchedulesEvents}
        visible={patientSchedulesEventsVisible}
        activeBranch={activeBranch}
        activeDate={activeDate}
        patientId={selectedPatientId}
        branchPath={branchPath}
        onSyncPatient={handleSyncRow}
      />
    </div>
  );
};

export default Ok2GoTable;
